import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { formatUsd } from "../../shared/money";
import {
  DEFAULT_THEME_COLOR,
  isThemeColor,
  normalizeThemeColor,
  THEME_COLOR_PRESETS,
  themeCssVariables,
  type ThemeColor,
} from "../../shared/theme";
import type { PublicState } from "../../shared/types";
import { createCheckout, getPublicConfig } from "../lib/api";
import { TurnstileWidget } from "./TurnstileWidget";

function sessionToken(): string {
  const key = "dontpress-checkout-session";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(key, created);
  return created;
}

interface CheckoutDialogProps {
  state: PublicState;
  onClose: () => void;
  onStale: () => void;
}

export function CheckoutDialog({ state, onClose, onStale }: CheckoutDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const minimumDollars = state.currentPriceCents / 100;
  const [amount, setAmount] = useState(String(minimumDollars));
  const [displayName, setDisplayName] = useState("");
  const [message, setMessage] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const initialThemeColor = state.currentHolder?.themeColor ?? DEFAULT_THEME_COLOR;
  const [themeColor, setThemeColor] = useState<ThemeColor>(initialThemeColor);
  const [themeColorInput, setThemeColorInput] = useState(initialThemeColor.toUpperCase());
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [serviceAccepted, setServiceAccepted] = useState(false);
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedDollars = Number(amount);
  const validAmount =
    Number.isSafeInteger(selectedDollars) &&
    selectedDollars >= minimumDollars &&
    selectedDollars <= 999_999;
  const selectedAmountCents = validAmount ? selectedDollars * 100 : 0;
  const nextMinimum = validAmount ? selectedAmountCents + 100 : state.currentPriceCents + 100;
  const validThemeColor = isThemeColor(themeColorInput.trim());
  const previewThemeStyle = themeCssVariables(themeColor) as CSSProperties;

  const chooseThemeColor = (value: string) => {
    const normalized = normalizeThemeColor(value, themeColor);
    setThemeColor(normalized);
    setThemeColorInput(normalized.toUpperCase());
  };

  useEffect(() => {
    void getPublicConfig()
      .then((config) => setSiteKey(config.turnstileSiteKey))
      .catch(() => setError("Checkout configuration is unavailable."));
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
  }, []);

  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback((messageValue: string) => setError(messageValue), []);

  const hostname = useMemo(() => {
    try {
      return destinationUrl ? new URL(destinationUrl).hostname : null;
    } catch {
      return null;
    }
  }, [destinationUrl]);

  return (
    <dialog
      className="checkout-dialog"
      ref={dialogRef}
      style={previewThemeStyle}
      aria-labelledby="checkout-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!submitting) onClose();
      }}
      onClose={() => {
        if (!submitting) onClose();
      }}
    >
      <form
        className="checkout-layout"
        onSubmit={(event) => {
          event.preventDefault();
          if (
            !validAmount ||
            !validThemeColor ||
            !termsAccepted ||
            !serviceAccepted ||
            !turnstileToken
          ) {
            setError(
              "Complete the amount, site color, both consent boxes, and human verification.",
            );
            return;
          }
          setSubmitting(true);
          setError(null);
          void createCheckout({
            expectedVersion: state.version,
            selectedAmountCents,
            displayName,
            publicMessage: message,
            destinationUrl,
            theme: "signal-red",
            themeColor,
            termsAccepted: true,
            immediateServiceAccepted: true,
            turnstileToken,
            sessionToken: sessionToken(),
          })
            .then(({ url }) => window.location.assign(url))
            .catch((caught: unknown) => {
              const status =
                typeof caught === "object" && caught !== null && "status" in caught
                  ? Number(caught.status)
                  : 0;
              if (status === 409) onStale();
              setError(
                caught instanceof Error
                  ? caught.message
                  : "THE BUTTON DIDN'T MOVE. No payment was captured.",
              );
              setSubmitting(false);
              setTurnstileToken("");
            });
        }}
      >
        <div className="checkout-form-panel">
          <div className="dialog-heading">
            <div>
              <p className="section-kicker">YOU ARE ABOUT TO PRESS IT.</p>
              <h2 id="checkout-title">Press #{state.nextPressNumber}</h2>
            </div>
            <button className="dialog-close" type="button" onClick={onClose} disabled={submitting}>
              CLOSE
            </button>
          </div>

          <div className="field-grid">
            <label className="field">
              <span>Your press amount (USD)</span>
              <span className="money-input">
                <b aria-hidden="true">$</b>
                <input
                  name="amount"
                  type="number"
                  min={minimumDollars}
                  max={999_999}
                  step={1}
                  inputMode="numeric"
                  value={amount}
                  aria-describedby="amount-help amount-error"
                  aria-invalid={!validAmount}
                  onChange={(event) => setAmount(event.target.value)}
                  required
                />
              </span>
              <small id="amount-help">
                Minimum {formatUsd(state.currentPriceCents)}. Whole dollars only. Your amount plus
                $1 becomes the next minimum.
              </small>
              {!validAmount ? (
                <small className="field-error" id="amount-error">
                  Enter a whole-dollar amount from {formatUsd(state.currentPriceCents)} to $999,999.
                </small>
              ) : null}
            </label>

            <label className="field">
              <span>Display name</span>
              <input
                name="displayName"
                type="text"
                minLength={2}
                maxLength={40}
                autoComplete="organization"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
              />
              <small>2 to 40 characters. Person, alias, product, or brand.</small>
            </label>

            <label className="field field-wide">
              <span>Public message</span>
              <textarea
                name="publicMessage"
                maxLength={140}
                rows={3}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
              <small>
                {Array.from(message).length}/140. Plain text. Put links in the next field.
              </small>
            </label>

            <label className="field field-wide">
              <span>Destination URL</span>
              <input
                name="destinationUrl"
                type="url"
                inputMode="url"
                placeholder="https://example.com"
                value={destinationUrl}
                onChange={(event) => setDestinationUrl(event.target.value)}
              />
              <small>Optional. HTTPS only. The hostname is shown publicly.</small>
            </label>
          </div>

          <fieldset className="color-picker">
            <legend>Site color</legend>
            <p>
              Your color takes over the entire site: button, holder panel, links, highlights, and
              surfaces.
            </p>
            <div className="color-picker-controls">
              <label className="color-well">
                <span>OPEN COLOR PICKER</span>
                <input
                  type="color"
                  aria-label="Choose site color"
                  value={themeColor}
                  onChange={(event) => chooseThemeColor(event.target.value)}
                />
              </label>
              <label className="hex-color-field">
                <span>Hex color</span>
                <input
                  name="themeColor"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={themeColorInput}
                  aria-invalid={!validThemeColor}
                  aria-describedby="theme-color-help theme-color-error"
                  onBlur={() => {
                    if (!validThemeColor) setThemeColorInput(themeColor.toUpperCase());
                  }}
                  onChange={(event) => {
                    const next = event.target.value.toUpperCase();
                    setThemeColorInput(next);
                    if (isThemeColor(next.trim())) setThemeColor(normalizeThemeColor(next));
                  }}
                  required
                />
              </label>
            </div>
            <div className="color-presets" aria-label="Color presets">
              {THEME_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.color}
                  type="button"
                  aria-label={`Use ${preset.name} ${preset.color.toUpperCase()}`}
                  aria-pressed={themeColor === preset.color}
                  style={{ backgroundColor: preset.color }}
                  onClick={() => chooseThemeColor(preset.color)}
                />
              ))}
            </div>
            <small id="theme-color-help">
              Pick any six-digit hex color. Accessible light and dark tones are generated from it.
            </small>
            {!validThemeColor ? (
              <small className="field-error" id="theme-color-error">
                Enter a color in #RRGGBB format.
              </small>
            ) : null}
          </fieldset>

          <div className="consent-stack">
            <label>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
                required
              />
              <span>
                I am at least 18 years old and accept the <a href="/rules">Rules</a>,{" "}
                <a href="/terms">Terms</a>, <a href="/privacy">Privacy Policy</a>,{" "}
                <a href="/refunds">Refund Policy</a> and{" "}
                <a href="/acceptable-use">Acceptable Use Policy</a>.
              </span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={serviceAccepted}
                onChange={(event) => setServiceAccepted(event.target.checked)}
                required
              />
              <span>
                I request the digital display and site-wide color service to begin immediately after
                payment is captured and understand that both can be replaced without a guaranteed
                duration.
              </span>
            </label>
            <p>Mandatory consumer rights remain unaffected.</p>
          </div>

          {siteKey === undefined ? (
            <p className="turnstile-loading" role="status">
              Loading human verification.
            </p>
          ) : (
            <TurnstileWidget
              siteKey={siteKey}
              onToken={handleTurnstileToken}
              onError={handleTurnstileError}
            />
          )}
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <button className="checkout-submit" type="submit" disabled={submitting}>
            {submitting
              ? "OPENING SECURE CHECKOUT"
              : `CONTINUE WITH ${formatUsd(selectedAmountCents || state.currentPriceCents)}`}
          </button>
        </div>

        <aside className="checkout-preview" aria-label="Press preview">
          <p className="placement-label">PREVIEW · FULL SITE TAKEOVER</p>
          <p className="preview-number">PRESS #{state.nextPressNumber}</p>
          <h3>{displayName.trim() || "YOUR DISPLAY NAME"}</h3>
          <p>{message.trim() || "Your public message can appear here."}</p>
          {hostname ? <span>VISIT {hostname.toUpperCase()}</span> : null}
          <p className="preview-color">
            <span style={{ backgroundColor: themeColor }} aria-hidden="true" />
            SITE COLOR {themeColor.toUpperCase()}
          </p>
          <dl>
            <div>
              <dt>YOUR PRESS</dt>
              <dd>{formatUsd(selectedAmountCents || state.currentPriceCents)}</dd>
            </div>
            <div>
              <dt>NEXT MINIMUM</dt>
              <dd>{formatUsd(nextMinimum)}</dd>
            </div>
          </dl>
          <p className="preview-warning">
            If another valid payment claims this version first, your payment will not be captured.
            Your bank may temporarily show an authorization.
          </p>
        </aside>
      </form>
    </dialog>
  );
}
